import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import {
  setChatVisible,
  setChatTriggerOverrides,
  clearChatTriggerOverrides,
} from "@/redux/reducers/chat";
import {
  cancelAllPendingScheduledTriggers,
  CHAT_PROACTIVE_SESSION_RESET,
  enqueueFirstWinsChatTrigger,
  runWithSuppressedProactiveModalCloseSessionReset,
} from "@/hooks/chat/helpers/chatTriggerCoordinator";
import { CHAT_TYPES } from "@/js/constants/chat";
import {
  safeCloseMicroModalById,
  openProactiveChatDialog,
  trackProactiveChatModalOpened,
} from "@/hooks/chat/helpers/safeMicroModal";
import { triggerAnimation } from "@/hooks/chat/helpers/chatbotHelpers";
import { trackEvent } from "@/js/helpers/tracking";

import { useIdleTrigger } from "./useIdleTrigger";
import { useAddressInputTrigger } from "./useAddressInputTrigger";
import { useCustomEventTrigger } from "./useCustomEventTrigger";
import { useReturnToTopTrigger } from "./useReturnToTopTrigger";
import { useProductCardIdleTrigger } from "./useProductCardIdleTrigger";

export const useChatTriggers = (chatConfig) => {
  const { chatType } = chatConfig;
  const triggers = chatConfig?.triggers;
  const lastTriggerFired = useRef(null);
  /** First real trigger wins the page session; no other proactive trigger or modal until `chatConfig` changes. */
  const proactiveSessionConsumedRef = useRef(false);
  /** Bumps on each ProactiveV2 modal intent so stale rAF chains never show after a newer intent. */
  const proactiveV2ModalEpochRef = useRef(0);
  const dispatch = useDispatch();
  const [sessionEpoch, setSessionEpoch] = useState(0);
  const [proactiveV2TriggersConsumed, setProactiveV2TriggersConsumed] =
    useState(false);

  useEffect(() => {
    proactiveV2ModalEpochRef.current += 1;
    proactiveSessionConsumedRef.current = false;
    setProactiveV2TriggersConsumed(false);
    safeCloseMicroModalById();
    dispatch(clearChatTriggerOverrides());
  }, [chatConfig, dispatch]);

  useEffect(() => {
    const onSessionReset = () => {
      proactiveV2ModalEpochRef.current += 1;
      cancelAllPendingScheduledTriggers();
      lastTriggerFired.current = null;
      /* Keep overrides + consumed flags so Kore copy and one-trigger-per-session hold after dismiss. */
      setSessionEpoch((n) => n + 1);
    };
    document.addEventListener(CHAT_PROACTIVE_SESSION_RESET, onSessionReset);
    return () =>
      document.removeEventListener(
        CHAT_PROACTIVE_SESSION_RESET,
        onSessionReset,
      );
  }, [dispatch]);

  /**
   * Executes one chat trigger. Clears sibling countdown timers first so only one
   * wins. Hooks wrap calls in `enqueueFirstWinsChatTrigger` for same-turn races.
   *
   * @returns {boolean} `false` when the run was skipped (so hooks can avoid marking `hasTriggered`).
   */
  const runTriggerChat = useCallback(
    (triggerName, options = {}) => {
      const { popModal, triggerKey } = options;
      const showProactiveModal = popModal !== false;
      const triggerEntry = triggerKey ? triggers?.[triggerKey] : null;

      if (chatType === CHAT_TYPES.NONE && options?.isProxyEvent) {
        if (lastTriggerFired.current === null) {
          trackEvent({
            action: "elementViewed",
            data: {
              elementType: "PROXY CHAT",
              position: `PROXY CHAT:${triggerName} trigger`,
            },
          });
          lastTriggerFired.current = triggerName;
        }
        return false;
      }

      if (proactiveSessionConsumedRef.current) {
        return false;
      }

      proactiveSessionConsumedRef.current = true;
      setProactiveV2TriggersConsumed(true);

      cancelAllPendingScheduledTriggers();
      const animate = chatConfig?.animate;

      if (triggerEntry) {
        const overrides = { triggerKey };
        if (triggerEntry.proactive !== undefined)
          overrides.proactive = triggerEntry.proactive;
        if (triggerEntry.proactiveModalContent !== undefined)
          overrides.proactiveModalContent = triggerEntry.proactiveModalContent;
        dispatch(setChatTriggerOverrides(overrides));
      } else {
        dispatch(clearChatTriggerOverrides());
      }

      if (
        chatType === CHAT_TYPES.PROACTIVE_V2 &&
        showProactiveModal &&
        triggerEntry
      ) {
        proactiveV2ModalEpochRef.current += 1;
        const epoch = proactiveV2ModalEpochRef.current;
        const animateSnapshot = animate;
        enqueueFirstWinsChatTrigger(() => {
          if (epoch !== proactiveV2ModalEpochRef.current) return;
          runWithSuppressedProactiveModalCloseSessionReset(() => {
            safeCloseMicroModalById();
          });
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (epoch !== proactiveV2ModalEpochRef.current) return;
              if (openProactiveChatDialog()) {
                trackProactiveChatModalOpened();
              }
              triggerAnimation(animateSnapshot);
            });
          });
        });
        cancelAllPendingScheduledTriggers();
      }

      lastTriggerFired.current = triggerName;

      dispatch(setChatVisible(true));

      if (chatType === CHAT_TYPES.PROACTIVE) {
        triggerAnimation(animate);
      }

      if (chatType === CHAT_TYPES.GIGA && typeof window !== "undefined") {
        const path = window.location?.pathname ?? "";
        const storageKey = "frdcGigaChatIconAnimation";
        let triggeredPages = {};
        try {
          triggeredPages = JSON.parse(
            sessionStorage.getItem(storageKey) || "{}",
          );
        } catch {
          triggeredPages = {};
        }
        if (!triggeredPages[path]) {
          triggeredPages[path] = true;
          sessionStorage.setItem(storageKey, JSON.stringify(triggeredPages));
          triggerAnimation(animate);
        }
      }

      return true;
    },
    [dispatch, chatType, chatConfig, triggers],
  );

  const triggersEnabled = (key) =>
    triggers?.[key] !== undefined && !proactiveV2TriggersConsumed;

  useIdleTrigger({
    enabled: triggersEnabled("idle"),
    timeout: triggers?.idle?.timeout,
    popModal: triggers?.idle?.popModal,
    triggerChat: runTriggerChat,
    triggerKey: "idle",
    sessionEpoch,
  });

  useAddressInputTrigger({
    enabled: triggersEnabled("addressEntry"),
    timeout: triggers?.addressEntry?.timeout,
    popModal: triggers?.addressEntry?.popModal,
    triggerChat: runTriggerChat,
    triggerKey: "addressEntry",
    sessionEpoch,
  });

  useCustomEventTrigger({
    enabled: triggersEnabled("faqs"),
    timeout: triggers?.faqs?.timeout,
    popModal: triggers?.faqs?.popModal,
    triggerChat: runTriggerChat,
    eventName: "faq-clicked",
    triggerSource: "FAQs",
    triggerKey: "faqs",
    sessionEpoch,
  });

  useReturnToTopTrigger({
    enabled: triggersEnabled("returnedToTop"),
    timeout: triggers?.returnedToTop?.timeout,
    popModal: triggers?.returnedToTop?.popModal,
    triggerChat: runTriggerChat,
    triggerKey: "returnedToTop",
    sessionEpoch,
  });

  useProductCardIdleTrigger({
    enabled: triggersEnabled("productCardIdle"),
    timeout: triggers?.productCardIdle?.timeout,
    popModal: triggers?.productCardIdle?.popModal,
    triggerChat: runTriggerChat,
    triggerKey: "productCardIdle",
    sessionEpoch,
  });

  return lastTriggerFired;
};
